import React, { useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { api } from "../../lib/api";
import { PolicyAccessFields } from "./PolicyAccessFields";

const INPUT = "mt-1.5 w-full px-3 py-2 rounded-md border border-slate-200 text-sm";
const ACCEPT = ".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export const PolicyUploadModal = ({ onClose, onCreated, categories, options, user }) => {
  const isHM = user.access_role === "House Manager";
  const hmHomes = user.assigned_homes?.length ? user.assigned_homes : [user.assigned_home].filter(Boolean);
  const [form, setForm] = useState({
    title: "", category: categories[0]?.name || "", description: "", owner: "", version_label: "v1",
    effective_date: "", review_date: "",
    home_scope: isHM ? hmHomes : [], permitted_roles: options.default_permitted_roles || [], all_roles: false,
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const canManageAccess = options.capabilities?.Configure;

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a PDF or Word (.docx) file");
    if (file.size > 25 * 1024 * 1024) return toast.error("File exceeds the 25 MB limit");
    if (!form.title.trim()) return toast.error("Title is required");
    if (isHM && !form.home_scope.length) return toast.error("Select at least one of your homes");
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(form).forEach(([k, v]) => fd.append(k, Array.isArray(v) ? v.join(",") : String(v)));
    setBusy(true);
    try {
      const { data } = await api.post("/policies", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ex = data.version.extraction_status;
      toast.success(ex === "complete" ? "Policy uploaded as Draft" : `Uploaded as Draft — text extraction ${ex}. Review it on the policy page.`);
      onCreated(data.policy);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="policy-upload-modal">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 space-y-4 my-6">
        <div className="flex items-center justify-between">
          <h2 className="text-h3">Upload policy</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="policy-upload-close"><X size={18} /></button>
        </div>
        <p className="text-xs text-slate-500">New policies start as <b>Draft</b> and are leadership-only until a policy manager sets the audience and approves them.</p>

        <label className="block text-xs font-semibold text-slate-600">Document (PDF or .docx, max 25 MB)
          <input type="file" accept={ACCEPT} onChange={(e) => setFile(e.target.files?.[0] || null)}
            className={`${INPUT} file:mr-3 file:px-3 file:py-1 file:rounded file:border-0 file:bg-sky-50 file:text-sky-800`} data-testid="policy-file-input" />
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-slate-600 md:col-span-2">Title
            <input value={form.title} onChange={set("title")} className={INPUT} data-testid="policy-title-input" required /></label>
          <label className="text-xs font-semibold text-slate-600">Category
            <select value={form.category} onChange={set("category")} className={INPUT} data-testid="policy-category-select">
              {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select></label>
          <label className="text-xs font-semibold text-slate-600">Owner (person / department)
            <input value={form.owner} onChange={set("owner")} className={INPUT} data-testid="policy-owner-input" /></label>
          <label className="text-xs font-semibold text-slate-600 md:col-span-2">Description
            <textarea value={form.description} onChange={set("description")} rows={2} className={INPUT} data-testid="policy-description-input" /></label>
          <label className="text-xs font-semibold text-slate-600">Version label
            <input value={form.version_label} onChange={set("version_label")} className={INPUT} data-testid="policy-version-input" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-semibold text-slate-600">Effective date
              <input type="date" value={form.effective_date} onChange={set("effective_date")} className={INPUT} data-testid="policy-effective-input" /></label>
            <label className="text-xs font-semibold text-slate-600">Review date
              <input type="date" value={form.review_date} onChange={set("review_date")} className={INPUT} data-testid="policy-review-input" /></label>
          </div>
        </div>

        <PolicyAccessFields value={form} onChange={(v) => setForm((f) => ({ ...f, ...v }))} options={options}
          homes={isHM ? hmHomes : options.homes} canManageRoles={canManageAccess} compact />

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-slate-200" data-testid="policy-upload-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="px-4 py-2 text-sm rounded-md bg-sky-700 text-white hover:bg-sky-800 disabled:opacity-50 inline-flex items-center gap-2" data-testid="policy-upload-submit">
            <Upload size={15} /> {busy ? "Uploading…" : "Upload as Draft"}
          </button>
        </div>
      </form>
    </div>
  );
};
